import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { ColumnDef } from "@tanstack/react-table";
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useConversations } from "../hooks/use-conversation-data";
import { getConversationsTableColumns } from "./columns/conversations-columns";

interface ConversationsTableContextValue {
  data: ConversationListItemResponse[];
  columns: ColumnDef<ConversationListItemResponse, unknown>[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
  focusedConversationId: ConversationId | null;
  focusedConversation: ConversationListItemResponse | null;
  setFocusedConversationId: (id: ConversationId | null) => void;
  navigateUp: () => void;
  navigateDown: () => void;
  actionMenuOpenForId: ConversationId | null;
  setActionMenuOpenForId: (id: ConversationId | null) => void;
}

const ConversationsTableContext =
  createContext<ConversationsTableContextValue | null>(null);

export function useConversationsTableContext(): ConversationsTableContextValue {
  const context = useContext(ConversationsTableContext);
  if (!context) {
    throw new Error(
      "useConversationsTableContext must be used within a ConversationsTableProvider",
    );
  }
  return context;
}

export const ConversationsTableProvider = memo(
  function ConversationsTableProvider({ children }: { children: ReactNode }) {
    // Data is already filtered via useQueryOptions() inside useConversations()
    const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } =
      useConversations();

    const [focusedConversationId, setFocusedConversationId] =
      useState<ConversationId | null>(null);

    const [actionMenuOpenForId, setActionMenuOpenForId] =
      useState<ConversationId | null>(null);

    const columns = useMemo(
      () => getConversationsTableColumns({ enablePinning: true }),
      [],
    );

    const focusedConversation = useMemo(() => {
      if (!focusedConversationId) {
        return null;
      }
      return data.find((c) => c.id === focusedConversationId) ?? null;
    }, [data, focusedConversationId]);

    const navigateUp = useCallback(() => {
      if (data.length === 0) {
        return;
      }
      if (!focusedConversationId) {
        setFocusedConversationId(data[data.length - 1].id);
        return;
      }
      const currentIndex = data.findIndex(
        (c) => c.id === focusedConversationId,
      );
      if (currentIndex <= 0) {
        return;
      }
      setFocusedConversationId(data[currentIndex - 1].id);
    }, [data, focusedConversationId]);

    const navigateDown = useCallback(() => {
      if (data.length === 0) {
        return;
      }
      if (!focusedConversationId) {
        setFocusedConversationId(data[0].id);
        return;
      }
      const currentIndex = data.findIndex(
        (c) => c.id === focusedConversationId,
      );
      if (currentIndex === -1 || currentIndex >= data.length - 1) {
        return;
      }
      setFocusedConversationId(data[currentIndex + 1].id);
    }, [data, focusedConversationId]);

    const value = useMemo<ConversationsTableContextValue>(
      () => ({
        data,
        columns,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        isLoading,
        focusedConversationId,
        focusedConversation,
        setFocusedConversationId,
        navigateUp,
        navigateDown,
        actionMenuOpenForId,
        setActionMenuOpenForId,
      }),
      [
        data,
        columns,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        isLoading,
        focusedConversationId,
        focusedConversation,
        navigateUp,
        navigateDown,
        actionMenuOpenForId,
      ],
    );

    return (
      <ConversationsTableContext.Provider value={value}>
        {children}
      </ConversationsTableContext.Provider>
    );
  },
);
